

import { Risolto_Task_Bool_Exp, useViewsInvoiceDisputes_AssignInvoiceDisputeMutation, ViewsInvoiceDisputes_TasksDocument, ViewsInvoiceDisputes_TasksQueryVariables } from '@/generated/graphql';
import InvoiceDisputeHelper from '@/helpers/invoice-dispute';
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import { IDisputesState } from '@/store/modules/disputes';
import Filters from './components/Filters.vue';
import gql from 'graphql-tag';

/**
 * Returns [{
 * 		recipient: grouped by recipient,
 *		invoice_disputes: list of invoice disputes,
 *		assignee: earliest assignee,
 *		from: start date,
 *		until: earliest until,
 *      disputeReason: reason for dispute,
 *      totalOpenAmount: sum of all open amounts,
 *      oldestDueDate: earliest due date among invoices
 * }]
 */
function groupTasks(tasks) {
	let groupedTasks: any[] = [];
	for (let task of tasks) {
		let recipient = task.invoice_dispute_by_reference.recipient;
		let invoice_dispute = task.invoice_dispute_by_reference.reference;
		let assignee = task.assignee_by_reference;
		let from = task.invoice_dispute_by_reference.from;
		let until = task.invoice_dispute_by_reference.until;
		let disputeCode = task.invoice_dispute_by_reference.dispute_code_by_reference?.code || '';
		let invoice = task.invoice_dispute_by_reference.invoice;
		let openAmount = invoice?.open_amount || 0;
		let issueDate = invoice?.issue_date;
		let invoiceDueDate = invoice?.due_date;
		let amount_currency = invoice?.amount_currency;

		if (!groupedTasks[recipient.reference]) {
			groupedTasks[recipient.reference] = {
				recipient: recipient,
				invoice_disputes: [],
				assignee,
				from,
				until,
				amount_currency,
				disputeReason: disputeCode,
				totalOpenAmount: 0,
				oldestDueDate: invoiceDueDate,
				oldestIssueDate: issueDate
			};
		}

		// Update the grouped task
		const currentGroup = groupedTasks[recipient.reference];

		// Add invoice dispute reference
		currentGroup.invoice_disputes = [
			...currentGroup.invoice_disputes,
			invoice_dispute
		];

		// Sum up open amounts
		currentGroup.totalOpenAmount += openAmount;

		// Track oldest due date
		if (invoiceDueDate && (!currentGroup.oldestDueDate || invoiceDueDate < currentGroup.oldestDueDate)) {
			currentGroup.oldestDueDate = invoiceDueDate;
			currentGroup.oldestIssueDate = issueDate;
		}
	}

	return Object.values(groupedTasks);
}

gql`
mutation ViewsInvoiceDisputes_assignInvoiceDispute (
  $participant: uuid!
  $legal_entity: uuid # Unused but unsure as to why
  $references: [uuid!]!
  $assignee: uuid # allow 'null' values
) {
  update_risolto_task(
      where: {
        participant: { _eq: $participant }
        invoice_dispute: { _in: $references }
      },
      _set: {
        assignee: $assignee
      }
    ) {
      affected_rows
      returning {
        __typename
        reference
        status
      }
    }
}
`;

export default Vue.extend({
	apollo: {
		tasks: {
			query: ViewsInvoiceDisputes_TasksDocument,
			variables(): ViewsInvoiceDisputes_TasksQueryVariables {
				return {
					where: this.queryVars.where
				};
			},
			skip() {
				return !this.queryVars.where;
			}
		}
	},
	components: {
		Filters
	},
	data() {
		return {
			selectedTasks: [],
			tasks: [],
			queryVars: {
				where: null
			}
		};
	},
	computed: {
		...mapGetters(['participantUsers']),
		...mapState('disputes', {
			filterDisputeCodes: (state: IDisputesState) => state.filterDisputeCodes,
			filterAssignee: (state: IDisputesState) => state.filterAssignee,
			filterDue: (state: IDisputesState) => state.filterDue
		}),
		groupedTasks() {
			return groupTasks(this.tasks);
		},
		where(): Risolto_Task_Bool_Exp {
			let where: Risolto_Task_Bool_Exp = {};
			where['participant'] = { _eq: this.$store.state.risolto.selectedParticipantStore };
			if (this.$store.state.risolto.selectedLegalEntityStore) {
				where['_or'] = [
					{ legal_entity: { _is_null: true } },
					{ legal_entity: { _eq: this.$store.state.risolto.selectedLegalEntityStore } }
				];
			}
			where['type'] = { _eq: 'DISPUTE' };
			where['status'] = { _eq: 'OPEN' };

			if (this.filterDisputeCodes && this.filterDisputeCodes.length > 0) {
				where['invoice_dispute_by_reference'] = {
					dispute_code_by_reference: {
						code: { _in: this.filterDisputeCodes }
					}
				};
			}

			if (this.filterAssignee) {
				if (this.filterAssignee === 'unassigned') {
					where['assignee'] = { _is_null: true };
				} else if (this.filterAssignee !== 'all') {
					where['assignee'] = { _eq: this.filterAssignee };
				}
			}

			if (this.filterDue) {
				where['due_date'] = { _lte: this.filterDue };
			}

			return where;
		}
	},
	created() {
		this.queryVars = {
			...this.queryVars,
			where: this.where
		};
	},
	methods: {
		handleFilter() {
			this.queryVars = {
				...this.queryVars,
				where: this.where
			};
		},
		handleCurrentChange(row) {
			if (row == null) {
				return;
			}

			this.$router.push({
				name: 'invoice-disputes.recipient',
				params: {
					participant: this.$store.state.risolto.selectedParticipantStore
				},
				query: {
					legalEntity: row.recipient.legal_entity,
					recipient: row.recipient.reference
				}
			});
		},
		handleSelectionChange(rows) {
			this.selectedTasks = rows;
		},
		async handleAssignTheLot(assignee) {
			const variables = {
				participant: this.$store.state.risolto.selectedParticipantStore,
				references: this.selectedTasks.map(task => task.invoice_disputes).flat(),
				assignee: assignee === 'unassigned' ? null : assignee
			};
			const { mutate } = useViewsInvoiceDisputes_AssignInvoiceDisputeMutation({
				variables: variables
			});
			await mutate();
			this.$apollo.queries.tasks.refetch();
		},
		isDue(row) {
			return InvoiceDisputeHelper.isDue(row.until);
		},
		isOverdue(row) {
			return InvoiceDisputeHelper.isOverDue(row.until);
		}
	}
});
